<template>
  <section class="project-section">
    <header class="section-header">
      <h3 class="section-title">{{ sectionTitle }}</h3>
      <p class="section-description">{{ sectionDescription }}</p>
    </header>

    <div v-if="collectionDetails.length === 0" class="empty-state">
      <svg class="w-16 h-16 text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="1.5"
          d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
        />
      </svg>
      <p class="text-gray-500">{{ $t('Collections.No collections available') }}</p>
    </div>

    <div v-else class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-6">
      <div
        v-for="collection in collectionDetails"
        :key="collection.value"
        class="collection-card group"
        :class="{
          'collection-card--selected': selectedCollectionRef === collection.value,
        }"
        @click="selectCollection(collection.value)"
        :data-cy="'collection-card-' + collection.value"
      >
        <!-- Selection indicator -->
        <div class="absolute top-3 right-3 z-10">
          <div
            class="selection-indicator"
            :class="{ 'selection-indicator--active': selectedCollectionRef === collection.value }"
          >
            <svg
              v-if="selectedCollectionRef === collection.value"
              class="w-4 h-4 text-white"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fill-rule="evenodd"
                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                clip-rule="evenodd"
              />
            </svg>
          </div>
        </div>

        <!-- Cover image -->
        <div class="collection-cover" :style="getCoverStyle(collection)">
          <div class="collection-cover-overlay"></div>
        </div>

        <!-- Collection info -->
        <div class="collection-content">
          <h4 class="collection-title">{{ collection.label || 'Untitled Collection' }}</h4>
          <p v-if="collection.description" class="collection-description">
            {{ collection.description }}
          </p>

          <div class="collection-meta">
            <div class="collection-stat">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                />
              </svg>
              <span>{{ collection.count || 0 }} {{ $t('Shared.actions') }}</span>
            </div>
            <div v-if="collection.updatedAt" class="collection-stat">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                />
              </svg>
              <span>{{ formatDate(collection.updatedAt) }}</span>
            </div>
          </div>
        </div>

        <!-- Hidden radio for accessibility -->
        <input
          type="radio"
          :id="`collection-${collection.value}`"
          :value="collection.value"
          v-model="selectedCollectionRef"
          class="sr-only"
          name="collection-selection"
          data-cy="collection-radio"
        >
      </div>
    </div>

    <p v-if="validationErrors[errorKey] || validationErrorMessage" class="validation-error">
      {{ validationErrors[errorKey] || validationErrorMessage }}
    </p>
  </section>
</template>

<script>
import dayjs from 'dayjs'

export default {
  name: 'ProjectSectionCollections',
  props: {
    allowedCollections: {
      type: Array,
      required: true,
    },
    selectedCollection: {
      type: [Number, null],
      required: true,
    },
    validationErrors: {
      type: Object,
      default: () => ({}),
    },
    errorKey: {
      type: String,
      default: 'collectionId',
    },
    sectionTitle: {
      type: String,
      default: '',
    },
    sectionDescription: {
      type: String,
      default: '',
    },
  },
  emits: ['selectedCollectionUpdated'],
  data () {
    return {
      selectedCollectionRef: this.selectedCollection || null,
      validationErrorMessage: null,
    }
  },
  computed: {
    collectionDetails () {
      // Enhance the collection options with full data from the store
      const actionCollections = this.$store.getters['action-collections/list']
      const questionCollections = this.$store.getters['question-collections/list']

      return this.allowedCollections.map((option) => {
        // Try to find the full collection data
        const actionCollection = actionCollections.find((c) => c.id === option.value)
        const questionCollection = questionCollections.find((c) => c.id === option.value)
        const fullCollection = actionCollection || questionCollection

        return {
          ...option,
          description: fullCollection?.description || '',
          media: fullCollection?.media || null,
          count: fullCollection?.standardActionsCount || fullCollection?.questionsCount || '0',
          updatedAt: fullCollection?.updatedAt || null,
        }
      })
    },
  },
  watch: {
    allowedCollections: {
      handler (newVal) {
        console.log('allowedCollections', newVal)
        if (
          this.selectedCollectionRef &&
          !newVal.find((collection) => collection.value === this.selectedCollectionRef)
        ) {
          this.selectedCollectionRef = newVal[0]?.value || null
        }
      },
      immediate: true,
    },
    selectedCollectionRef (newVal) {
      if (this.validate()) {
        this.$emit('selectedCollectionUpdated', newVal)
      }
    },
  },
  methods: {
    selectCollection (collectionId) {
      this.selectedCollectionRef = collectionId
    },
    validate () {
      const selectedIsValid = this.validateSelectedCollection(this.selectedCollectionRef)
      return selectedIsValid
    },
    validateSelectedCollection (selectedCollection) {
      if (!selectedCollection) {
        this.validationErrorMessage = this.$t(
          'Organizations.Popups.Validation_error_no_collections_selected',
        )
        return false
      }
      this.validationErrorMessage = null
      return true
    },
    getCoverStyle (collection) {
      if (collection.media?.location) {
        return {
          backgroundImage: `url('${this.$store.state.urls.appApiRest}${collection.media.location}')`,
        }
      }
      return {}
    },
    formatDate (date) {
      return dayjs(date).format('YYYY-MM-DD')
    },
  },
}
</script>

<style scoped>
.project-section {
  @apply py-2;
}

.section-header {
  @apply mb-2;
}

.section-title {
  @apply text-xl font-semibold text-gray-800 mb-2;
}

.section-description {
  @apply text-gray-500 leading-relaxed;
}

.empty-state {
  @apply flex flex-col items-center justify-center py-12 bg-gray-50 rounded-xl border-2 border-dashed border-gray-200;
}

.collection-card {
  @apply relative bg-white rounded-xl border-2 border-gray-200 overflow-hidden cursor-pointer;
  @apply transition-all duration-200 ease-out;
  @apply hover:border-gray-300 hover:shadow-lg hover:-translate-y-0.5;
}

.collection-card--selected {
  @apply border-green-500 shadow-md;
  box-shadow: 0 0 0 2px rgba(16, 185, 129, 0.2);
}

.collection-card--selected:hover {
  @apply border-green-500;
}

.selection-indicator {
  @apply w-6 h-6 rounded-full border-2 border-gray-300 bg-white;
  @apply flex items-center justify-center;
  @apply transition-all duration-200;
}

.selection-indicator--active {
  @apply border-green-500 bg-green-500;
}

.collection-cover {
  @apply h-32 bg-gradient-to-br from-gray-100 to-gray-200 bg-cover bg-center relative;
}

.collection-cover-overlay {
  @apply absolute inset-0 bg-gradient-to-t from-black/20 to-transparent;
}

.collection-content {
  @apply p-4;
}

.collection-title {
  @apply font-semibold text-gray-800 mb-2;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
}

.collection-description {
  @apply text-sm text-gray-500 mb-3;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

.collection-meta {
  @apply flex flex-col gap-2 pt-3 border-t border-gray-100;
}

.collection-stat {
  @apply flex items-center gap-2 text-sm text-gray-500;
}

.collection-stat svg {
  @apply text-gray-400;
}

.validation-error {
  @apply text-sm text-red-500 mt-4 flex items-center gap-2;
}

.validation-error::before {
  content: '';
  @apply w-1.5 h-1.5 rounded-full bg-red-500;
}
</style>
